import { Badge, Box, Flex, Stack, StackDivider, Text } from "@chakra-ui/react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionAnswerChoice,
  AwardSubCriterionAnswerFormat,
  type AwardCriterionDto,
  type AwardCriterionType,
  type BidV2AwardCriterionResponseDto,
  type EventValueDto,
} from "../../../autogen/bff-api";
import {
  groupScorableAwardRows,
  priceFirst,
  scorableAwardRows,
  type ScorableAwardGroup,
  type ScorableAwardRow,
} from "../../../common/award-criteria/scorableAwardRows";
import { formatEstimatedValue } from "../portal/utils/displayFormatters";
import { formatPercent } from "../buyer/wizard/utils/awardCriteriaStatus";
import { AwardCriterionTypeBadge } from "./AwardCriterionTypeBadge";
import { ReadonlyAnswerCard, type ReadonlyAnswer } from "./ReadonlyAnswerCard";

interface Props {
  criteria: AwardCriterionDto[];
  responses: BidV2AwardCriterionResponseDto[];
  price: EventValueDto | null | undefined;
  answerLabel: string;
  notAnsweredLabel: string;
  /** Below the answer block — the buyer's evaluation injects the score and its justification here. */
  renderRowFooter?: (row: ScorableAwardRow) => ReactNode;
  /** Replaces the whole card for a price row, which is scored from the tendered price, not answered. */
  renderPriceRow?: (row: ScorableAwardRow) => ReactNode;
}

/**
 * The delivered award-criterion answers, grouped by criterion: one heading per criterion and one card
 * per scorable row beneath it. Shared by the supplier's read-only offer and the buyer's bid detail so
 * a change lands in one place; the buyer adds its scoring through the two render slots.
 */
export const AwardCriteriaAnswers = ({
  criteria,
  responses,
  price,
  answerLabel,
  notAnsweredLabel,
  renderRowFooter,
  renderPriceRow,
}: Props) => {
  const { t } = useTranslation();
  const groups = priceFirst(
    groupScorableAwardRows(scorableAwardRows(criteria, (type: AwardCriterionType) => t(type))),
  );
  const responsesById = new Map(responses.map((response) => [response.criterionId, response] as const));

  return (
    <Stack spacing="6">
      {groups.map((group) => (
        <Box
          key={group.key}
          bg="smBackground"
          border="1px solid"
          borderColor="smBorder"
          rounded="xl"
          overflow="hidden"
        >
          <GroupHeading group={group} />
          <Stack spacing="0" divider={<StackDivider borderColor="smBorder" />}>
            {group.rows.map((row) =>
              row.isPrice && renderPriceRow ? (
                <Box key={row.id}>{renderPriceRow(row)}</Box>
              ) : (
                <ReadonlyAnswerCard
                  key={row.id}
                  chrome="plain"
                  // A criterion that is its own row would only repeat the heading right above it.
                  title={(group.hasOwnHeading && row.name?.trim()) || group.label}
                  titleSuffix={<RowWeight row={row} group={group} />}
                  descriptionHtml={row.description || null}
                  answerLabel={answerLabel}
                  answer={toReadonlyAnswer(row, responsesById.get(row.id), price)}
                  notAnsweredLabel={notAnsweredLabel}
                  evidence={(responsesById.get(row.id)?.attachments ?? []).map((doc) => ({
                    id: doc.id,
                    name: doc.name,
                  }))}
                  // Points on a price row are computed from the tendered price; the BFF rejects any
                  // attempt to set them, so the row is never given somewhere to try.
                  footer={row.isPrice ? undefined : renderRowFooter?.(row)}
                />
              ),
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const GroupHeading = ({ group }: { group: ScorableAwardGroup }) => {
  const { t } = useTranslation();
  return (
    <Flex
      align="center"
      gap="3"
      flexWrap="wrap"
      bg="smBackgroundSecondary"
      borderBottom="1px solid"
      borderColor="smBorder"
      px={{ base: "4", md: "6" }}
      py="3"
    >
      <AwardCriterionTypeBadge type={group.type} />
      <Text fontWeight="semibold" color="smText">
        {group.label}
      </Text>
      {group.weightPercent != null && (
        <Badge bg="smStatusOkBg" color="smStatusOkText" textTransform="none" borderRadius="md" px="2" py="0.5">
          {formatPercent(group.weightPercent)} {t("of the total")}
        </Badge>
      )}
    </Flex>
  );
};

// A criterion without sub-criteria already states its weight in the group heading, so only a
// sub-criterion carries one of its own -- and it is a share of the criterion, not of the total.
const RowWeight = ({ row, group }: { row: ScorableAwardRow; group: ScorableAwardGroup }) => {
  const { t } = useTranslation();
  return (
    <Flex align="center" gap="2" flexWrap="wrap">
      {group.hasOwnHeading && row.ownWeightPercent != null && (
        <Badge bg="smStatusOkBg" color="smStatusOkText" textTransform="none" borderRadius="md" px="2" py="0.5">
          {t("{{weight}} within {{criterion}}", {
            weight: formatPercent(row.ownWeightPercent),
            criterion: group.label,
          })}
        </Badge>
      )}
      {row.requiresDocumentation && (
        <Badge bg="smWarningSoft" color="smWarning" textTransform="none" borderRadius="md" px="2" py="0.5">
          {t("Documentation required")}
        </Badge>
      )}
    </Flex>
  );
};

const toReadonlyAnswer = (
  row: ScorableAwardRow,
  response: BidV2AwardCriterionResponseDto | undefined,
  price: EventValueDto | null | undefined,
): ReadonlyAnswer => {
  // Every price row is scored from the one tendered price, so that is what each of them shows.
  if (row.isPrice) {
    return price?.value != null
      ? { kind: "text", value: formatEstimatedValue(price.value, price.currency) }
      : { kind: "empty" };
  }
  switch (row.answerFormat) {
    case AwardSubCriterionAnswerFormat.FreeText:
      return response?.text?.trim() ? { kind: "text", value: response.text } : { kind: "empty" };
    case AwardSubCriterionAnswerFormat.Number:
      return response?.number != null ? { kind: "number", value: response.number } : { kind: "empty" };
    case AwardSubCriterionAnswerFormat.YesNoPartial:
      return response?.choice
        ? {
            kind: "choice",
            value: choiceValue(response.choice),
            // Only "delvis" carries one; the other two answer for themselves.
            elaborationHtml: response.partialElaboration,
          }
        : { kind: "empty" };
    case AwardSubCriterionAnswerFormat.Document:
      // The files are listed as evidence below the answer block, so the block itself only says whether
      // anything arrived.
      return (response?.attachments ?? []).length > 0 ? { kind: "text", value: "" } : { kind: "empty" };
  }
};

const choiceValue = (choice: AwardCriterionAnswerChoice): "YES" | "NO" | "PARTIAL" => {
  switch (choice) {
    case AwardCriterionAnswerChoice.Yes:
      return "YES";
    case AwardCriterionAnswerChoice.No:
      return "NO";
    case AwardCriterionAnswerChoice.Partial:
      return "PARTIAL";
  }
};
